import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Alert, Spin, Skeleton, Card } from 'antd';
import { ReloadOutlined, DashboardOutlined } from '@ant-design/icons';
import { useDashboard } from '../../hooks/useDashboard';
import UserInfo from './UserInfo';
import GraficasFavoritasWidget from './GraficasFavoritasWidget';
import GraficasPersonalizadasWidget from './GraficasPersonalizadasWidget';
import DashboardDev from './DashboardDev';
import './Dashboard.scss';

const Dashboard = () => {
  const [isDevelopment, setIsDevelopment] = useState(false);
  // const [showSkeleton, setShowSkeleton] = useState(true); // Variable no utilizada - removida
  const [hasInitialized, setHasInitialized] = useState(false);
  
  const {
    loading,
    initialLoad,
    error,
    graficasFavoritas,
    contadores,
    userInfo,
    lastUpdated,
    refresh,
    handleToggleFavorite,
    isStale
  } = useDashboard();

  // Marcar como inicializado apenas se monte el componente
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
    }
  }, [hasInitialized]);

  // Controlar cuándo ocultar el skeleton
  useEffect(() => {
    if (hasInitialized && !initialLoad) {
      // Una vez que termina la carga inicial, esperar un poco antes de ocultar el skeleton
      const timer = setTimeout(() => {
        // setShowSkeleton(false); // Variable no utilizada - removida
        
        // Decidir si mostrar modo desarrollo
        if (error || (!loading && graficasFavoritas.length === 0 && contadores.proyectos === 0)) {
          setIsDevelopment(true);
        }
      }, 800); // Mantener el skeleton un poco más tiempo
      
      return () => clearTimeout(timer);
    }
  }, [hasInitialized, initialLoad, error, graficasFavoritas, contadores, loading]);


  // Si estamos en modo desarrollo, mostrar el dashboard de desarrollo
  if (isDevelopment) {
    return <DashboardDev />;
  }

  // Mostrar skeleton durante actualizaciones
  if (loading && !graficasFavoritas.length && !initialLoad) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <Row justify="space-between" align="middle">
            <Col>
              <h1><DashboardOutlined /> Dashboard</h1>
              <p style={{ color: '#666', margin: 0 }}>Actualizando información...</p>
            </Col>
            <Col>
              <Skeleton.Button active size="small" />
            </Col>
          </Row>
        </div>

        <div className="dashboard-content">
          <Row gutter={[16, 16]}>
            {/* Gráficas Personalizadas */}
            <Col xs={24}>
              <Card style={{ height: '500px' }}>
                <Skeleton active paragraph={{ rows: 12 }} />
              </Card>
            </Col>

            {/* Gráficas Favoritas */}
            <Col xs={24} xl={12}>
              <Card style={{ height: '400px' }}>
                <Skeleton active paragraph={{ rows: 8 }} />
              </Card>
            </Col>

            {/* Contadores */}
            <Col xs={24} xl={12}>
              <Card style={{ height: '400px' }}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Card>
            </Col>

            {/* Últimos Sensores */}
            <Col xs={24} xl={12}>
              <Card style={{ height: '350px' }}>
                <Skeleton active paragraph={{ rows: 7 }} />
              </Card>
            </Col>

            {/* Consumo Energía */}
            <Col xs={24} xl={12}>
              <Card style={{ height: '350px' }}>
                <Skeleton active paragraph={{ rows: 7 }} />
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  if (error && !error.includes('Network Error') && !error.includes('404') && !error.includes('500')) {
    return (
      <div className="dashboard-error">
        <Alert
          message="Error al cargar el dashboard"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" type="primary" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="dashboard-header__title">
          <DashboardOutlined />
          <h1>Dashboard</h1>
        </div>
        
        <div className="dashboard-header__controls">
          <UserInfo userInfo={userInfo} lastUpdated={lastUpdated} />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={refresh}
            loading={loading}
            className={isStale ? 'stale-data' : ''}
          >
            {isStale ? 'Datos antiguos - Actualizar' : 'Actualizar'}
          </Button>
        </div>
      </div>

      {isStale && (
        <Alert
          message="Los datos pueden estar desactualizados"
          description="Última actualización hace más de 5 minutos. Considera actualizar los datos."
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Spin spinning={loading} tip="Actualizando datos...">
        <div className="dashboard-content">
          <Row gutter={[16, 16]}>
            {/* Fila principal: Gráficas personalizadas */}
            <Col xs={24}>
              <GraficasPersonalizadasWidget />
            </Col>

            {/* Fila superior: Gráficas favoritas */}
            <Col xs={24}>
              <GraficasFavoritasWidget
                graficas={graficasFavoritas}
                onToggleFavorite={handleToggleFavorite}
              />
            </Col>
          </Row>
        </div>
      </Spin>

      {/* Información adicional */}
      <div className="dashboard-footer">
        <div className="dashboard-footer__info">
          {lastUpdated && (
            <span>
              Última actualización: {new Date(lastUpdated).toLocaleString('es-ES')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
